import React, { useEffect, useState, useRef, useContext, useCallback } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import Moment from 'moment';
import { format as D3format } from 'd3';

import OffIcon from '@mui/icons-material/EmojiPeople';
import OnIcon from '@mui/icons-material/AccessibilityNew';

import { ProfileContext } from '../data/auth-service';
import useWindowSize from '../libraries/usewindowsize';
import DataContext from '../data/data-context';

import {
    teal,
    lavender,
    olive,
    skyBlue,
    pointlessPuple,
    pointlessPink,
    yellow,
    orange,
    purple,
    blue,
    pumpkin,
    red,
} from '../data/colors.js';

const useStyles = makeStyles(theme => ({
    root: {
    },
    score: {
        position: 'absolute',
        top: 10,
        backgroundColor: yellow[400],
        color: 'black',
        width: 200,
        height: 30,
        zIndex: 9999,
        padding: 5,
        paddingLeft: 10,
        '& > span': {
            fontWeight: 'bold',
            paddingLeft: 5,
        }
    },
    contrib: {
        position: 'absolute',
        fontSize: 12,
        fontWeight: 'bold',
        textAlign: 'center',
        cursor: 'pointer',
        zIndex: 9999,
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    offIcon: {
        transition: 'clip-path 550ms cubic-bezier(0.4, 0.0, 0.2, 1), -webkit-clip-path 550ms cubic-bezier(0.4, 0.0, 0.2, 1)',
        width: '100%',
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0
    },
    onIcon: {
        transition: 'clip-path 550ms cubic-bezier(0.4, 0.0, 0.2, 1), -webkit-clip-path 550ms cubic-bezier(0.4, 0.0, 0.2, 1)',
        width: '100%',
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0
    },
    player: {
        position: 'absolute',
        zIndex: 9999,
        width: 72,
        height: 72,
        display: 'inline-block',
        transition: 'all 0.05s',
    },
}));

const colors = [
    teal,
    lavender,
    olive,
    skyBlue,
    pointlessPuple,
    pointlessPink,
    // yellow,
    orange,
    purple,
    blue,
    pumpkin
];

const randomColor = () => {
    return colors[Math.floor(Math.random() * colors.length)][400];
};

// git shortlog -se | grep bricata.com
// value points:
// 42: contributed to this release,
// 11: contributed to any release
const contributors = [
    { name: 'Adam Pumphrey', value: 11, r: 36 },
    { name: 'Andrey Terentyev', value: 11, r: 36 },
    { name: 'Andrey Vovk', value: 42, r: 30 },
    { name: 'Bhanu Bikki', value: 11, r: 30 },
    { name: 'Carl Hughey', value: 42, r: 30 },
    { name: 'Daxa Patel', value: 11, r: 27 },

    { name: 'Galit Miller', value: 42, r: 28 },
    { name: 'Gene Savchuk', value: 11, r: 30 },
    { name: 'Gregory Ronin', value: 11, r: 33 },
    { name: 'Hasan Seirafi', value: 11, r: 30 },
    { name: 'Jason Ish', value: 11, r: 27 },
    { name: 'Joe Ciccone', value: 11, r: 30 },

    { name: 'Kalyn Charest', value: 42, r: 33 },
    { name: 'Mark Narduzzi', value: 11, r: 33 },
    { name: 'Mark Turton', value: 42, r: 28 },
    { name: 'Min Wang', value: 11, r: 27 },
    { name: 'Orville Pike', value: 11, r: 33 },
    { name: 'Ed Enson', value: 11, r: 26 },

    { name: 'Paul Casto', value: 11, r: 30 },
    { name: 'Satish Narayan', value: 11, r: 34 },
    { name: 'Shawn Speer', value: 42, r: 30 },
    { name: 'Tom Van Nostrand', value: 11, r: 35 },
    { name: 'Zach McGrenere', value: 42, r: 38 },
    { name: 'Andre Ludwig', value: 42, r: 33 },

    { name: 'Michael Szubka', value: 11, r: 32 },
    { name: 'Andrew Beard', value: 11, r: 33 },
    { name: 'Richard Cooper', value: 42, r: 33 },
];

const speed = 4;
const margin = 10;
const playerSpeed = 20;

const FortyTwo = () => {
    const classes = useStyles();
    const theme = useTheme();
    const animationRef = useRef();
    const playaRef = useRef();
    const { sidenav } = useContext(DataContext);
    const { width, height } = useWindowSize();
    const { updateProfile, getProfile } = useContext(ProfileContext);

    const [start] = useState(Moment());
    const [score, setScore] = useState(0);
    const [highscore, setHeighScore] = useState(getProfile('fortytwo').score || 0);
    const [cwidth, setCWidth] = useState(width);
    const [contrib, setContrib] = useState([]);

    const collision = useCallback((a, b) => {
        return (a.x < b.x + b.width &&
           a.x + a.width > b.x &&
           a.y < b.y + b.height &&
           a.height + a.y > b.y);
    }, []);

    useEffect(() => {
        // clear board after 30 minutes
        const start = Moment();
        const timer = setInterval(() => {
            if (Moment().diff(start, 'minutes') > 30) {
                clearInterval(timer);
                setContrib([]);
            }
            // apply time penalty
            setContrib(current => current.map(c => ({
                ...c,
                vy: Math.min(c.vy * (5 / 4), 15),
                vx: Math.min(c.vx * (5 / 4), 15),
            })));
        }, 10000);

        return () => {
            clearInterval(timer);
        };
    }, []);

    useEffect(() => {
        const s = contrib.reduce((a, c) => a + (c.score || 0), 0);
        if ((getProfile('fortytwo').score || 0) < s) updateProfile('fortytwo', { score: s });
        setScore(s);
        setHeighScore(current => Math.max(current, s));
    }, [contrib, getProfile, updateProfile]);

    // don't care about sidebar
    useEffect(() => {
        setCWidth(width + (sidenav.collapsed ? 0 : 220));
    }, [sidenav.collapsed, width]);

    useEffect(() => {
        if (cwidth < 600 || height < 600) return;

        animationRef.current && window.cancelAnimationFrame(animationRef.current);

        setContrib(contributors.map((c, i) => ({
            ...c,
            vx: 0,
            vy: 0,
            x: (i % 6) * (cwidth / 6),
            y: Math.floor(i / 6) * (height / 6),
            color: randomColor(),
        })));

        const move = () => {
            animationRef.current = window.requestAnimationFrame(move);

            const penalty = Math.floor(Moment().diff(Moment(start)) / 10);

            setContrib(current => {
                // first check for mob collisions
                current.forEach(c => {
                    // already fell or falling
                    if (c.fired) {
                        if (c.y >= height) return;
                        // let it drop to the ground
                        c.y += speed;
                        return;
                    }

                    // needs firing?
                    const m = document.getElementById(`mob_${c.name}`);
                    if (!m) return c;
                    const mrect = m.getBoundingClientRect();
                    if (playaRef.current) {
                        const prect = playaRef.current.getBoundingClientRect();
                        if (prect && prect.x + prect.y > 0) {
                            if (collision(prect, mrect) || collision(mrect, prect)) {
                                console.log('thank you', `${c.name}!`, Math.max(c.value * 1000 - penalty, 1), 'points');
                                c.fired = true;
                                c.score = Math.max(c.value * 1000 - penalty, 1);
                                return;
                            }
                        }
                    }

                    // get or initialize current position and velocity
                    const x = c.x || Math.floor(Math.random() * cwidth - margin);
                    let vx = c.vx || Math.floor(Math.random() * speed + 1);
                    const y = c.y || Math.floor(Math.random() * height - margin);
                    let vy = c.vy || Math.floor(Math.random() * speed + 1);

                    // check for collisions with other contribs
                    current.forEach(other => {
                        if (other.name === c.name) return;
                        const orect = document
                            .getElementById(`mob_${other.name}`)
                            .getBoundingClientRect();
                        if (collision(orect, mrect)) {
                            const v = { x: other.x - c.x, y: other.y - c.y };
                            const distance = Math.sqrt((other.x - c.x) * (other.x - c.x) +
                                (other.y - c.y) * (other.y - c.y));
                            const vNorm = { x: v.x / distance, y: v.y / distance };
                            const vRV = { x: c.vx - other.vx, y: c.vy - other.vy };
                            const speed = vRV.x * vNorm.x + vRV.y * vNorm.y;
                            if (speed < 0) return;

                            vx -= speed * vNorm.x;
                            vy -= speed * vNorm.y;
                            other.vx += speed * vNorm.x;
                            other.vy += speed * vNorm.y;
                        }
                    });
                    c.x = x + vx;
                    c.y = y + vy;
                    c.vx = vx;
                    c.vy = vy;
                });
                // check for wall collisions
                current.filter(c => !c.fired).forEach(c => {
                    let boostX = 0;
                    let boostY = 0;
                    if (c.x + c.vx > cwidth - margin) {
                        // bounce off right wall
                        boostX = -(c.x + c.vx + c.r - cwidth - margin);
                        c.vx = 0 - Math.abs(c.vx);
                    } else if (c.x + c.vx < margin) {
                        // bounce off left wall
                        boostX = margin - c.x + c.vx + c.r;
                        c.vx = Math.abs(c.vx);
                    }
                    if (c.y + c.vy > height - margin) {
                        // bounce off floor
                        boostY = -(c.y + c.vy - height - margin + c.r);
                        c.vy = 0 - Math.abs(c.vy);
                    } else if (c.y + c.vy < margin) {
                        // bounce off ceiling
                        boostY = margin - c.y + c.vy + c.r;
                        c.vy = Math.abs(c.vy);
                    }
                    c.x = c.x + c.vx + boostX;
                    c.y = c.y + c.vy + boostY;
                });
                return [...current];
            });
        };
        move();

        return () => {
            animationRef.current && window.cancelAnimationFrame(animationRef.current);
        };
    }, [cwidth, height, start, collision]);

    return cwidth > 0 ? (
        <React.Fragment>
            {contrib.map((c, i) =>
                <div key={`${c.name}`} id={`mob_${c.name}`}
                    className={clsx(classes.contrib)}
                    style={{
                        backgroundColor: c.fired && c.value === 11 ? yellow[400]
                            : c.fired && c.value === 42 ? red[400] : c.color,
                        color: c.fired ? 'black' : theme.palette.getContrastText(c.color),
                        top: c.y || 0,
                        left: c.x || 0,
                        width: c.r * 2,
                        height: c.r * 2,
                        borderRadius: c.r,
                    }}>
                    {c.name}
                </div>
            )}
            {contrib.length > 0 &&
                <Player ref={playaRef} width={cwidth} height={height}/>
            }
            {/* score */}
            <div className={classes.score} style={{
                left: cwidth - 150,
                backgroundColor: score >= highscore ? yellow[400] : olive[400]
            }}>
                Current Score:
                {/* <span>{score}</span> */}
                <span>{D3format(',')(score)}</span>
            </div>
            <div className={classes.score} style={{ left: cwidth - 350 }}>
                {/* High Score: <span>{highscore}</span> */}
                High Score: <span>{D3format(',')(highscore)}</span>
            </div>
        </React.Fragment>
    ) : null;
};

const Player = React.forwardRef((props, ref) => {
    const classes = useStyles();

    const [on, setOn] = useState(true);
    const [position, setPosition] = useState({ top: 500, left: 500 });
    const [color, setColor] = useState(randomColor());

    useEffect(() => {
        return () => {
            console.log('thanks for playing');
        };
    }, []);

    useEffect(() => {
        if (props.height === 0 || props.width === 0) return;

        const timer = setInterval(() => {
            setOn(current => !current);
            setColor(randomColor());
        }, 420);

        setPosition({ top: props.height / 2, left: props.width / 2 });

        const chase = e => {
            switch (e.code) {
                case 'KeyW':
                    setPosition(current => {
                        if (current.top - playerSpeed < 0) { return { ...current, top: props.height } }
                        return { ...current, top: current.top - playerSpeed };
                    });
                    break;
                case 'KeyA':
                    setPosition(current => {
                        if (current.left - playerSpeed < 0) { return { ...current, left: props.width } }
                        return { ...current, left: current.left - playerSpeed };
                    });
                    break;
                case 'KeyS':
                    setPosition(current => {
                        if (current.top + playerSpeed > props.height) { return { ...current, top: 0 } }
                        return { ...current, top: current.top + playerSpeed };
                    });
                    break;
                case 'KeyD':
                    setPosition(current => {
                        if (current.left + playerSpeed > props.width) { return { ...current, left: 0 } }
                        return { ...current, left: current.left + playerSpeed };
                    });
                    //break;
            }
        };

        document.addEventListener('keydown', chase);

        return () => {
            clearInterval(timer);
            document.removeEventListener('keydown', chase);
        };
    }, [props.height, props.width]);

    return props.width + props.height > 0 ? (
        <div className={classes.player} style={{ ...position }} ref={ref}>
            <OffIcon
                className={classes.offIcon}
                style={{ visibility: !on ? 'visible' : 'hidden', color }}/>
            <OnIcon
                className={classes.onIcon}
                style={{ visibility: on ? 'visible' : 'hidden', color }}/>
        </div>
    ) : undefined;
});
Player.propTypes = {
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
};

export {
    FortyTwo as default,
};
